/**
 * Маршруты.
 *
 * Пока идёт проверка сессии — показываем нейтральный экран, а не форму
 * входа: моргание «войдите» → «главная» у уже вошедшего человека выглядит
 * как ошибка.
 */
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui';
import { LoginPage } from './pages/Login';
import { ChangePasswordPage } from './pages/ChangePassword';
import { DashboardPage } from './pages/Dashboard';
import { WorkerHomePage } from './pages/WorkerHome';
import { TasksPage } from './pages/Tasks';
import { TaskDetailPage } from './pages/TaskDetail';
import { CreateTaskPage } from './pages/CreateTask';
import { VideoPage } from './pages/Video';
import { ReviewListPage } from './pages/ReviewList';
import { ReviewPage } from './pages/Review';
import { FinancePage } from './pages/Finance';
import { TeamPage } from './pages/Team';
import { TemplatesPage } from './pages/Templates';
import { ClientsPage } from './pages/Clients';
import { PenaltiesPage } from './pages/Penalties';
import { StoragePage } from './pages/Storage';
import { SettingsPage } from './pages/Settings';
import { ProfilePage } from './pages/Profile';
import { SharePage } from './pages/Share';
import { PortalPage } from './pages/Portal';

/**
 * Корень приложения.
 *
 * Страница клиента вынесена ПЕРЕД проверкой входа: заказчик открывает
 * ссылку без аккаунта, и требовать от него авторизации было бы нелепо.
 */
export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/s/:token" element={<SharePage />} />
        <Route path="/p/:token" element={<PortalPage />} />
        <Route path="*" element={<PrivateApp />} />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}

function PrivateApp() {
  const { user, loading, can, error, mustChangePassword } = useAuth();

  if (loading) {
    return (
      <div className="h-full grid place-items-center text-muted2">
        <div className="text-center">
          <div className="text-[22px] tracking-[0.02em] font-extrabold text-accent2 mb-3">
            Конторка
          </div>
          <div className="text-xs">Загрузка…</div>
        </div>
      </div>
    );
  }

  if (!user) return <LoginPage error={error} />;

  // Пока временный пароль не заменён, в систему не пускаем:
  // иначе его так и не сменят
  if (mustChangePassword) return <ChangePasswordPage />;

  // Админ и менеджер видят дашборд, исполнители — свою очередь.
  // Список из 156 ТЗ на главной у воркера бесполезен.
  const Home = can('video.review') ? DashboardPage : WorkerHomePage;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="tasks/new" element={<CreateTaskPage />} />
        <Route path="tasks/:id" element={<TaskDetailPage />} />
        <Route path="videos/:id" element={<VideoPage />} />
        <Route path="review" element={<ReviewListPage />} />
        <Route path="review/:id" element={<ReviewPage />} />
        <Route path="finance" element={<FinancePage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="penalties" element={<PenaltiesPage />} />
        <Route path="storage" element={<StoragePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
